import React, { useCallback, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import i18n from '@/i18n';
import {
  CANCELLATION_REASONS,
  cancellationReasonRequiresNote,
  getCancellationReasonLabel,
  isCancellationValid,
} from '@/constants/cancellationReasons';

import AppButton from '../buttons/AppButton';
import AppSelect from '../select/AppSelect';

/**
 * Dialog di annullamento ordine: sostituisce la conferma generica, perché la
 * causale è obbligatoria — confermare senza sceglierla non è previsto.
 *
 * Usage — imperativo (come `useAppConfirm`):
 *
 *   const askCancelReason = useCancelReason();
 *   const res = await askCancelReason({ count: 3 });
 *   if (!res) return;                       // annullato dall'utente
 *   await cancelOrders(ids, res);           // res = { reason, note }
 */
export default function CancelReasonDialog({
  open,
  count = 1,
  title,
  message,
  onConfirm,
  onCancel,
}) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');

  // Reset a ogni apertura: il dialog è montato una volta sola dal provider e
  // riusato da tutte le schermate — senza reset la causale precedente
  // resterebbe preselezionata sull'annullamento successivo.
  React.useEffect(() => {
    if (open) {
      setReason(null);
      setNote('');
    }
  }, [open]);

  const options = CANCELLATION_REASONS.map((r) => ({
    value: r.code,
    label: getCancellationReasonLabel(r.code),
  }));

  const noteRequired = cancellationReasonRequiresNote(reason?.value);
  const payload = { reason: reason?.value, note };
  const canConfirm = isCancellationValid(payload);

  const defaultTitle =
    count > 1
      ? i18n.t('annulla_n_viaggi', {
          count,
          defaultValue: `Annulla ${count} viaggi`,
        })
      : i18n.t('annulla_viaggio', { defaultValue: 'Annulla viaggio' });

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-container)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-danger)',
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <i className="fa-solid fa-ban" />
        </Box>
        <Box sx={{ fontSize: 16, fontWeight: 700, color: 'var(--color-primary)' }}>
          {title || defaultTitle}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important', pb: 1 }}>
        <Box sx={{ fontSize: 14, color: 'var(--color-on-surface-dim)', lineHeight: 1.6, mb: 2 }}>
          {message || i18n.t('modifica_non_reversibile')}
        </Box>

        <Box
          component="label"
          htmlFor="cancellation-reason"
          sx={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-on-surface-strong)',
            mb: 0.75,
          }}
        >
          {i18n.t('causale_annullamento', { defaultValue: 'Causale annullamento' })} *
        </Box>
        <AppSelect
          inputId="cancellation-reason"
          options={options}
          value={reason}
          onChange={setReason}
          placeholder={i18n.t('seleziona_causale', { defaultValue: 'Seleziona una causale…' })}
          isClearable={false}
        />

        <Box
          component="label"
          htmlFor="cancellation-note"
          sx={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-on-surface-strong)',
            mt: 2,
            mb: 0.75,
          }}
        >
          {i18n.t('nota', { defaultValue: 'Nota' })}
          {noteRequired ? ' *' : ''}
        </Box>
        <Box
          component="textarea"
          id="cancellation-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            noteRequired
              ? i18n.t('nota_obbligatoria', { defaultValue: 'Nota obbligatoria per "Altro"' })
              : i18n.t('nota_facoltativa', { defaultValue: 'Nota (facoltativa)' })
          }
          sx={{
            width: '100%',
            padding: 'var(--space-2)',
            border: '1px solid var(--color-input-border)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-body)',
            color: 'var(--color-on-surface-strong)',
            background: 'var(--color-surface)',
            resize: 'vertical',
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <AppButton mode="ghost" variant="neutral" size="sm" onClick={onCancel}>
          {i18n.t('no_annulla')}
        </AppButton>
        <AppButton
          mode="nav"
          variant="danger"
          size="sm"
          disabled={!canConfirm}
          onClick={() => onConfirm({ reason: reason.value, note: note.trim() })}
        >
          {i18n.t('si_elimina')}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Imperative hook ─────────────────────────────────────────────────────────

const CancelReasonContext = React.createContext(null);

export function CancelReasonProvider({ children }) {
  const [state, setState] = useState({ open: false, props: {}, resolve: null });

  const askCancelReason = useCallback((props = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, props, resolve });
    });
  }, []);

  const close = (result) => {
    state.resolve?.(result);
    setState((s) => ({ ...s, open: false, resolve: null }));
  };

  return (
    <CancelReasonContext.Provider value={askCancelReason}>
      {children}
      <CancelReasonDialog
        open={state.open}
        {...state.props}
        onConfirm={(result) => close(result)}
        onCancel={() => close(null)}
      />
    </CancelReasonContext.Provider>
  );
}

/**
 * Ritorna `{ reason, note }` se l'operatore conferma, `null` se annulla.
 * Va sempre chiamato PRIMA di cancellare: la causale è parte dell'operazione.
 */
export function useCancelReason() {
  const askCancelReason = React.useContext(CancelReasonContext);
  if (!askCancelReason) {
    throw new Error('useCancelReason must be used inside <CancelReasonProvider>');
  }
  return askCancelReason;
}
